/**
 * ReplenishmentFilterContext
 * Multi-select filter state scoped to the Replenishment module.
 * URL-synced via useURLFilters with r_ prefixed keys to avoid collision.
 */
import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useURLFilters, ParamValue } from '../../hooks/useURLFilters';

// ---- Types ----

export interface ReplenishmentDateRange {
  start: Date | null;
  end: Date | null;
}

export interface ReplenishmentFilterContextType {
  brands: string[];
  categories: string[];
  stores: string[];
  cities: string[];
  dateRange: ReplenishmentDateRange;

  setBrands: (v: string[]) => void;
  setCategories: (v: string[]) => void;
  setStores: (v: string[]) => void;
  setCities: (v: string[]) => void;
  setDateRange: (r: ReplenishmentDateRange) => void;
  clearAll: () => void;

  activeFilterCount: number;
}

// ---- Helpers ----

const PARAM_KEYS = ['r_brands', 'r_categories', 'r_stores', 'r_cities', 'r_from', 'r_to'];

function toStringArray(v: ParamValue): string[] {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.length > 0) return [v];
  return [];
}

function toDate(v: ParamValue): Date | null {
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// ---- Context ----

const ReplenishmentFilterCtx = createContext<ReplenishmentFilterContextType | undefined>(undefined);

export const ReplenishmentFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { params, setParam, clearAll: urlClearAll } = useURLFilters({ paramKeys: PARAM_KEYS, debounceMs: 300 });

  const brands = useMemo(() => toStringArray(params.r_brands), [params.r_brands]);
  const categories = useMemo(() => toStringArray(params.r_categories), [params.r_categories]);
  const stores = useMemo(() => toStringArray(params.r_stores), [params.r_stores]);
  const cities = useMemo(() => toStringArray(params.r_cities), [params.r_cities]);
  const dateRange = useMemo<ReplenishmentDateRange>(() => ({
    start: toDate(params.r_from),
    end: toDate(params.r_to),
  }), [params.r_from, params.r_to]);

  const setBrands = useCallback((v: string[]) => setParam('r_brands', v.length > 0 ? v : null), [setParam]);
  const setCategories = useCallback((v: string[]) => setParam('r_categories', v.length > 0 ? v : null), [setParam]);
  const setStores = useCallback((v: string[]) => setParam('r_stores', v.length > 0 ? v : null), [setParam]);
  const setCities = useCallback((v: string[]) => setParam('r_cities', v.length > 0 ? v : null), [setParam]);
  const setDateRange = useCallback((r: ReplenishmentDateRange) => {
    setParam('r_from', r.start);
    setParam('r_to', r.end);
  }, [setParam]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (brands.length > 0) count++;
    if (categories.length > 0) count++;
    if (stores.length > 0) count++;
    if (cities.length > 0) count++;
    if (dateRange.start || dateRange.end) count++;
    return count;
  }, [brands, categories, stores, cities, dateRange]);

  const value = useMemo<ReplenishmentFilterContextType>(() => ({
    brands, categories, stores, cities, dateRange,
    setBrands, setCategories, setStores, setCities, setDateRange,
    clearAll: urlClearAll,
    activeFilterCount,
  }), [brands, categories, stores, cities, dateRange, setBrands, setCategories, setStores, setCities, setDateRange, urlClearAll, activeFilterCount]);

  return (
    <ReplenishmentFilterCtx.Provider value={value}>
      {children}
    </ReplenishmentFilterCtx.Provider>
  );
};

export function useReplenishmentFilters(): ReplenishmentFilterContextType {
  const ctx = useContext(ReplenishmentFilterCtx);
  if (!ctx) {
    throw new Error('useReplenishmentFilters must be used within <ReplenishmentFilterProvider>');
  }
  return ctx;
}

export default ReplenishmentFilterProvider;
